import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import queryString from 'query-string';
import classNames from 'classnames';

import {
    Colours,
    useExperiment,
    scrollIntoView,
    MessageBanner,
    ExperimentSegments,
    MessageBannerVariants,
    navigateWindowTo,
    getLanguageFromLocale,
    ANALYTICS_KEY,
    ANALYTICS_ATTRIBUTE_NAME,
    useModal,
    CircleSpinner,
    AFFILIATE_QUERY_PARAM_KEYS,
    ModalFullScreenTakeoverStyles,
} from '@ratehub/base-ui';
import { LayoutRow } from '@ratehub/web-components';
import {
    HeaderBlock,
    HeaderDetailsBlock,
    CardDetailsAccordion,
    CardFooterSelector,
    CardComparisonList,
    HORIZONTAL_WHITE_SPACE,
    USER_REVIEWS_DOM_ID,
} from '@ratehub/cc-card-details';
import {
    EXPERIMENTS,
    CC_URL_SLUGS,
    INCENTIVE_VARIANTS,
    getCreditCardURL,
    CreditCardShape,
    COMMON_MESSAGES,
    getIncentiveVariant,
    GIFT_CARD_LEAD_QUERY_PARAMS,
    extractRhAnalyticsFromCreditCard,
    CreditCardIncentive,
    trackHeapCCRedirectEvent,
    getIncentiveFullScreenTakeoverStyles,
} from '@ratehub/cc-common';

import Layout from './Layout';


function CreditCardDetailsPage({
    cardInfo,
    cardComparisonList,
}) {
    const intl = useIntl();
    const languageCode = getLanguageFromLocale(intl.locale);
    const [ isUserReviewsExpanded, setIsUserReviewsExpanded ] = useState(false);
    const modal = useModal();
    const [ isFirstRender, setIsFirstRender ] = useState(true);

    const incentiveVariant = getIncentiveVariant({
        isIncentiveCompactPage: useExperiment(EXPERIMENTS.incentiveCompactPage)?.segment === ExperimentSegments.VARIANT_1,
    });
    const isAltTextCTA = useExperiment(EXPERIMENTS.altTextCTA)?.segment === ExperimentSegments.VARIANT_1;

    // Scroll to user reviews if the URL has a query param for it
    useEffect(() => {
        if (typeof window !== 'undefined' && queryString.parse(window.location.search).view === 'reviews') {
            // without _some_ delay, the final scroll position would often not match the preferred
            // scroll position at the top of User reviews, as happens via clicking; varies between browsers
            setTimeout(scrollToUserReviewsThenOpen, 90);
        }
    }, []);


    // Redirect or open gift card offer modal if URL query param indicates it is a landing page
    useEffect(() => {
        if (incentiveVariant !== null) {
            const queryParams = queryString.parse(window.location.search);
            const referrerURL = queryParams?.[AFFILIATE_QUERY_PARAM_KEYS.conversionReferrer];
            const affiliateId = queryParams?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId];

            const isCCRedirectionPage = queryParams?.[CC_REDIRECTION_PAGE_QUERY_PARAM_KEY] === 'true';
            const isGiftCardEnabled = !!cardInfo.giftCardOffer;

            if (isCCRedirectionPage && isGiftCardEnabled) {
                modal.open({
                    isContentFullWidth: true,
                    showBranding: false,
                    isCloseVisible: false,      // captive audience: if moving forward, you must convert
                    verticalAlignment: 'flex-start',

                    style: {
                        // since they can’t get to the details page from here, we should obscure all that is underneath
                        ...ModalFullScreenTakeoverStyles,
                        ...(incentiveVariant === INCENTIVE_VARIANTS.compactPage) && {
                            ...getIncentiveFullScreenTakeoverStyles(),  // with grape background at top
                        },
                    },

                    screenReaderLabel: (
                        <FormattedMessage
                            id="edb.cc.details-gift-card.screen-reader"
                            defaultMessage="Credit card gift offer"
                        />
                    ),

                    children: (
                        <CreditCardIncentive
                            data-test-name="gift-card-offer"
                            cardInfo={cardInfo}
                            onAcceptOrDecline={(additionalInfo) =>
                                redirectToApply({
                                    referrerURL,
                                    affiliateId,
                                    additionalInfo,
                                })
                            }
                            referrerURL={referrerURL}
                            className="rh-pt-2"
                        />
                    ),
                });
                setIsFirstRender(false);    // necessary if they refuse the offer
            } else if (isCCRedirectionPage) {
                redirectToApply(referrerURL, affiliateId);
            } else {
                setIsFirstRender(false);
            }
        }
    }, [ incentiveVariant ]);

    async function scrollToUserReviewsThenOpen() {
        // Council Bluffs, IA’s GOOGLE-CLOUD-PLATFORM has a knack for requesting user reviews when there are
        // no user reviews, so we’re going to be skeptical of this request.
        const target = document.querySelector(`#${USER_REVIEWS_DOM_ID}`);
        if (target) {
            await scrollIntoView(target);
            setIsUserReviewsExpanded(true);
        }
    }

    function redirectToApply({ referrerURL, affiliateId, additionalInfo = {} } = {}) {
        const { applyRedirectURL, isSponsored } = cardInfo;
        const { email, firstName, lastName, isOfferAccepted } = additionalInfo;

        trackHeapCCRedirectEvent({ referrerURL, affiliateId, additionalInfo, cardInfo });

        if (isOfferAccepted) {
            navigateWindowTo(applyRedirectURL, {
                isRedirect: true,
                queryParams: {
                    sponsored: isSponsored,
                    ...(isOfferAccepted && {
                        [GIFT_CARD_LEAD_QUERY_PARAMS.email]: email,
                        [GIFT_CARD_LEAD_QUERY_PARAMS.firstName]: firstName,
                        // make sure to send an empty string to confirm intentional blankness
                        [GIFT_CARD_LEAD_QUERY_PARAMS.lastName]: lastName ?? '',
                        [GIFT_CARD_LEAD_QUERY_PARAMS.isGiftCardLead]: isOfferAccepted,
                    }),
                },
            });
        } else {
            navigateWindowTo(applyRedirectURL, {
                isRedirect: true,
                queryParams: {
                    sponsored: isSponsored,
                },
            });
        }
    }

    function onUserReviewsToggle() {
        setIsUserReviewsExpanded(!isUserReviewsExpanded);
    }

    // now that we have an intl, provide our CTA
    if (cardComparisonList) {
        const applyText = isAltTextCTA
            ? intl.formatMessage(COMMON_MESSAGES.ctaButtonTextAlt)
            : intl.formatMessage(COMMON_MESSAGES.ctaButtonText);

        cardComparisonList.forEach(card => card.applyText = card.applyText ?? applyText);
    }


    return (
        <Layout
            metaTitle={intl.formatMessage(MESSAGES.pageTitle, cardInfo)}
            metaDescription={intl.formatMessage(MESSAGES.metaDescription, cardInfo)}
            hideBackgroundImage={false}
            isGridDisabled
        >
            <If condition={isFirstRender}>
                <div className="spinner-wrapper rh-position-relative">
                    {/* "fixed" places it mid-screen, vs. at the top, which may not be visible */}
                    <CircleSpinner position="fixed" />
                </div>
            </If>
            <LayoutRow className="rh-py-4">
                <section className="rh-flex-grow-1">
                    <h1 className="rh-title-3xl rh-mt-1_5 rh-mb-4">
                        {
                            <FormattedMessage
                                id={'cc.card-details.content-title'}
                                defaultMessage={'Overview of the {name}'}
                                values={{
                                    name: cardInfo.name,
                                }}
                            />
                        }
                    </h1>

                    <If condition={cardInfo.isHidden}>
                        <MessageBanner
                            className="rh-mb-3"
                            iconKey="exclamation-mark"
                            variant={MessageBannerVariants.SUCCESS}
                            title={(
                                <FormattedMessage
                                    id="cc.card-details.no-longer-available-title"
                                    defaultMessage="Unfortunately, this card is no longer available on ratehub.ca."
                                />
                            )}
                            message={(
                                <FormattedMessage
                                    id="cc.card-details.no-longer-available-text"
                                    defaultMessage="See what other cards are available."
                                />
                            )}
                            ctaMessage={(
                                <FormattedMessage
                                    id="cc.card-details.help-me-find"
                                    defaultMessage="Help me find my card"
                                />
                            )}
                            onClick={() => navigateWindowTo(getCreditCardURL(languageCode, CC_URL_SLUGS.CARDFINDER))}
                        />
                    </If>

                    <section
                        ref={self => self && (self[ANALYTICS_KEY] = extractRhAnalyticsFromCreditCard(cardInfo))}
                        className={classNames('rh-mb-3 rh-bg-coconut rh-b-1px-solid-stone', ANALYTICS_ATTRIBUTE_NAME)}
                    >
                        <HeaderBlock
                            cardInfo={cardInfo}
                            onReviewsClick={cardInfo.reviewCount ? scrollToUserReviewsThenOpen : undefined}
                        />

                        <HeaderDetailsBlock cardInfo={cardInfo} />

                        <CardFooterContainer className="rh-bg-stone-lightest">
                            <CardFooterSelector cardInfo={cardInfo} />
                        </CardFooterContainer>
                    </section>
                </section>
            </LayoutRow>

            <If condition={cardComparisonList}>
                <LayoutRow
                    className="rh-py-6"
                    backgroundColour={Colours.STONE_LIGHT}
                    overflow="hidden"
                >
                    <CardComparisonList
                        productType={cardInfo.productType}
                        cards={cardComparisonList}
                    />
                </LayoutRow>
            </If>

            <LayoutRow
                className={classNames(
                    cardInfo.isMonetized ? 'rh-pt-0' : 'rh-pt-4',
                    'rh-pb-4',
                )}
            >
                <CardDetailsAccordion
                    className="rh-flex-grow-1"
                    cardInfo={cardInfo}
                    isUserReviewsExpanded={isUserReviewsExpanded}
                    onUserReviewsToggle={onUserReviewsToggle}
                />
            </LayoutRow>
        </Layout>
    );
}

CreditCardDetailsPage.propTypes = {
    cardInfo: CreditCardShape.isRequired,
    cardComparisonList: PropTypes.arrayOf(PropTypes.shape({
        propertyType: PropTypes.string,
        title: PropTypes.string,
        isSponsored: PropTypes.bool,

        applyHref: PropTypes.string,
        applyText: PropTypes.string,    // expected to still be empty after getInitialProps()
        imageSrc: PropTypes.string,
        imageAlt: PropTypes.string,

        description: PropTypes.string,
        providerSlug: PropTypes.string,
        detailsHref: PropTypes.string,
        isMonetized: PropTypes.bool,
    })),
};

CreditCardDetailsPage.defaultProps = {
    cardComparisonList: undefined,
};


const CC_REDIRECTION_PAGE_QUERY_PARAM_KEY = 'isCCRedirectionPage';

const CardFooterContainer = styled.div`
    padding: 0 ${HORIZONTAL_WHITE_SPACE};
`;

const MESSAGES = defineMessages({
    pageTitle: {
        id: 'cc.card-details.page-title',
        defaultMessage: '{name} - overview | Ratehub.ca',
    },
    metaDescription: {
        id: 'cc.card-details.meta-description',
        defaultMessage: 'Learn everything you need to know about {name} and if it’s right for you. Get details on rewards, rates, and more, and apply online now.',
    },

    ctaHeading: {
        id: 'cc.card.cta-heading',
        defaultMessage: 'We’ve helped over 1 million Canadians compare the best credit cards',
    },
    ctaMessage: {
        id: 'cc.card.cta-message',
        defaultMessage: 'Whether you want cash back or travel points, lounge access benefits or roadside assistance perks – there’s a credit card out there that’s right for you. Find it in under 2 minutes.',
    },
    ctaButtonText: {
        id: 'cc.card.cta-button-text',
        defaultMessage: 'Let’s get started!',
    },
    ctaURL: {
        id: 'cc.card.cta-URL',
        defaultMessage: '/credit-cards/scenario-chooser',
    },
});

export { CC_REDIRECTION_PAGE_QUERY_PARAM_KEY };
export default CreditCardDetailsPage;
